/*
  Cart Tier Gifts — 10th anniversary "Paliers de cadeaux" watcher.
  Rules: .claude/rules/cart-tier-gifts.md

  Pure cart-value reconciliation: a tier gift line exists in the cart if and
  only if the qualifying cart value (non-gift lines, after line-level
  discounts, excluding gift cards and the merchant-configured product types
  — see isExcludedProduct below) meets that tier's threshold. Gifts auto-add
  when a threshold is crossed and auto-remove when the cart drops back
  below it.

  config.tiers already excludes any gift_tier block outside its own
  start_date/end_date window — that filtering happens server-side in
  snippets/cart-drawer.liquid before this script ever sees the JSON, same
  as cart-tier-progress.liquid's active_tier_ids. This script never needs
  its own date logic.

  Server-side pricing is owned by the tier-gift-discount Shopify Function —
  this script only manages line presence, never prices.
*/
(function () {
  'use strict';

  var configEl = document.querySelector('[data-cart-tier-config]');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  if (!config || !config.enabled || !Array.isArray(config.tiers) || !config.tiers.length) return;

  /* CAD-only promotion — mirrors the Liquid render guard and the function */
  if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active !== config.currency) return;

  function route(path) {
    if (window.LVTheme && window.LVTheme.route) return window.LVTheme.route(path);
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root + path.replace(/^\/+/, '');
  }

  function thresholdMap(cfg) {
    var map = {};
    (cfg.tiers || []).forEach(function (tier) {
      (tier.variants || []).forEach(function (variantId) {
        map[variantId] = tier.threshold;
      });
    });
    return map;
  }

  var inflight = Promise.resolve();
  var reconciling = false;
  var pending = false;
  var suppress = false;
  var unavailable = {}; /* variantId -> true when /cart/add.js rejected it this session */
  var debounceTimer = null;

  function isGiftLine(item) {
    var props = item.properties || {};
    return props._tier_gift === 'true';
  }

  function isAnyGift(item) {
    var props = item.properties || {};
    return props._free_gift === 'true' || props._tier_gift === 'true';
  }

  /*
   * Gift cards (item.gift_card === true, a native /cart.js field) always
   * count as excluded. Product types are merchant-configurable via the
   * header section's "Excluded product types" setting
   * (config.excludedProductTypes, a raw comma-separated string — split and
   * trimmed here rather than in Liquid, see snippets/cart-drawer.liquid),
   * defaulting to "Coffret". Same exclusion definition as
   * cart-threshold-auto-add.js's qualifying() and cart-tier-progress.liquid,
   * per the merchant's explicit "no mixing promotions" rule.
   *
   * IMPORTANT: the tier-gift-discount Shopify Function has its own
   * hardcoded copy of this excluded-types list (Functions can't read theme
   * settings) — changing the header setting does NOT change what the
   * Function actually zeroes to $0. See .claude/rules/cart-tier-gifts.md.
   *
   * `|| 'Coffret'` is deliberately NOT used here — an explicitly blanked
   * setting comes through as '', which `||` would treat the same as
   * "missing" and wrongly re-apply the old default, making it impossible
   * for a merchant to turn exclusion off entirely (same trap as Liquid's
   * `default` filter, see cart-drawer.liquid). Only a genuinely missing
   * field (e.g. a stale cached page from before this field existed) falls
   * back to 'Coffret'.
   */
  function excludedTypesFor(cfg) {
    var raw = typeof cfg.excludedProductTypes === 'string' ? cfg.excludedProductTypes : 'Coffret';
    return raw.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
  }

  function isExcludedProduct(item, excludedProductTypes) {
    if (item.gift_card === true) return true;
    return excludedProductTypes.indexOf(item.product_type) !== -1;
  }

  /*
   * cfgOverride: a FRESH config parsed from a just-fetched copy of the
   * drawer (see ensureForCheckout below). Without it the page-load config
   * is used, exactly as before. Returns a promise that resolves once this
   * pass (and any gift add/remove it triggered) is finished.
   */
  function reconcile(cfgOverride) {
    if (reconciling) {
      pending = true;
      if (!cfgOverride) return inflight;
      return inflight.then(function () { return reconcile(cfgOverride); });
    }
    reconciling = true;

    var cfg = cfgOverride || config;
    var thresholdByVariant = thresholdMap(cfg);
    var excludedProductTypes = excludedTypesFor(cfg);

    inflight = fetch(route('cart.js'))
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(function (cart) {
        if (cart.currency && cart.currency !== cfg.currency) return null;

        /* Qualifying value (cents): non-gift lines after line-level discounts,
           excluding coffrets and gift cards (see isExcludedProduct above) */
        var qualifying = 0;
        cart.items.forEach(function (item) {
          if (isAnyGift(item)) return;
          if (isExcludedProduct(item, excludedProductTypes)) return;
          qualifying += item.final_line_price;
        });

        var actualByVariant = {};
        cart.items.forEach(function (item) {
          if (isGiftLine(item)) actualByVariant[item.variant_id] = item;
        });

        var desired = {};
        (cfg.tiers || []).forEach(function (tier) {
          if (qualifying < tier.threshold) return;
          (tier.variants || []).forEach(function (variantId) {
            if (!unavailable[variantId]) desired[variantId] = true;
          });
        });

        var toAdd = Object.keys(desired).filter(function (variantId) {
          return !actualByVariant[variantId];
        }).map(Number);

        var toRemove = [];
        var qtyFix = [];
        Object.keys(actualByVariant).forEach(function (variantId) {
          var item = actualByVariant[variantId];
          if (!desired[variantId]) {
            toRemove.push(item.key);
          } else if (item.quantity !== 1) {
            qtyFix.push(item.key);
          }
        });

        if (!toAdd.length && !toRemove.length && !qtyFix.length) return null; /* fixpoint */

        suppress = true;
        var ops = Promise.resolve();

        toRemove.forEach(function (key) {
          ops = ops.then(function () { return changeItem(key, 0); });
        });
        qtyFix.forEach(function (key) {
          ops = ops.then(function () { return changeItem(key, 1); });
        });
        if (toAdd.length) {
          ops = ops.then(function () {
            return addItems(toAdd.map(function (variantId) {
              return {
                id: variantId,
                quantity: 1,
                properties: {
                  _free_gift: 'true',
                  _tier_gift: 'true',
                  _tier: String(thresholdByVariant[variantId] / 100)
                }
              };
            })).catch(function () {
              /* 422 = sold out (or otherwise unaddable) — skip for this session */
              toAdd.forEach(function (variantId) { unavailable[variantId] = true; });
            });
          });
        }

        return ops.then(function () {
          if (window.cartDrawer && window.cartDrawer.refresh) return window.cartDrawer.refresh();
        });
      })
      .catch(function () { /* network error — retry on the next cart:updated */ })
      .then(function () {
        suppress = false;
        reconciling = false;
        if (pending) {
          pending = false;
          reconcile();
        }
      });
    return inflight;
  }

  /*
   * Called by the cart drawer's checkout button (snippets/cart-drawer.liquid
   * bindCheckoutHandler) with a config parsed from a FRESH server render, so
   * a customer who clicks "Checkout" before the normal cart:updated pass has
   * finished still gets the gift they qualified for -- and a tab left open
   * past a promo's end date can never add one (the fresh config no longer
   * lists that tier). Resolves even on failure; checkout must never wait on
   * or be blocked by this.
   */
  window.LVTierGifts = {
    ensureForCheckout: function (freshConfig) {
      if (!freshConfig || !freshConfig.enabled || !Array.isArray(freshConfig.tiers)) {
        return Promise.resolve();
      }
      return reconcile(freshConfig).catch(function () {});
    }
  };

  function changeItem(key, quantity) {
    if (window.LVTheme && window.LVTheme.cart) {
      return window.LVTheme.cart.changeItem(key, quantity, { refreshDrawer: false });
    }
    return fetch(route('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (response) {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  function addItems(items) {
    if (window.LVTheme && window.LVTheme.cart) {
      return window.LVTheme.cart.addItems(items, { refreshDrawer: false });
    }
    return fetch(route('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  document.addEventListener('cart:updated', function () {
    if (suppress) return;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(function () { reconcile(); }, 150);
  });

  /* Stale carts: reconcile once on load (e.g. threshold config changed since last visit) */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }
})();
